import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ChevronLeft, ChevronRight, RotateCw, X } from 'lucide';

import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';

import Icon from 'common/components/Icon';
import MuiDialog from 'common/components/MuiDialog';
import MuiLoading from 'common/components/MuiLoading';
import { nextQuarterTurn } from 'common/components/imageZoom';
import useService from 'common/hook/useService';
import { lastEatingWeekDay, menuWeekDayFromJsDay } from 'modules/map/components/daily-dish/sample-meal/eatingDays';
import { coverPhoto, sortSampleMealPhotos } from 'modules/map/components/daily-dish/sample-meal/photoSort';
import type { SampleMealDay } from 'modules/map/components/daily-dish/sample-meal/types';

interface Props {
  open: boolean;
  onClose: () => void;
  siteCode: string;
  weekStart: string | null;
  /** Ngày cuối thực đơn rút gọn: 6 = thứ 6, 7 = thứ 7. Chủ nhật không hiện. */
  menuItems?: Array<{ weekDay?: number | null }> | null;
}

const WEEKDAYS = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
const GRID_GAP = 16;

/** 5 ngày: 3 + 2 căn giữa. 6 ngày: 3 + 3. 4 ngày: ô 2×2. */
function columnsFor(count: number) {
  if (count <= 1) return 1;
  if (count === 2 || count === 4) return 2;
  return 3;
}

function formatRange(days: SampleMealDay[]) {
  const dates = days
    .map((day) => (day.photo_date ? dayjs(day.photo_date) : null))
    .filter((date): date is dayjs.Dayjs => !!date && date.isValid());
  if (!dates.length) return null;
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (first.isSame(last, 'day')) return first.format('DD/MM/YYYY');
  if (first.year() === last.year()) {
    return `${first.format('DD/MM')} – ${last.format('DD/MM/YYYY')}`;
  }
  return `${first.format('DD/MM/YYYY')} – ${last.format('DD/MM/YYYY')}`;
}

const SampleMealPhotosDialog = (props: Props) => {
  const { open, onClose, siteCode, weekStart, menuItems } = props;
  const lastMenuWeekDay = lastEatingWeekDay(menuItems);
  const service = useService();
  const [viewerDay, setViewerDay] = useState<SampleMealDay | null>(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  /** Góc xem theo từng ảnh. Chỉ xoay lúc xem, không ghi đè file. */
  const [rotationByUrl, setRotationByUrl] = useState<Record<string, number>>({});

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: [siteCode, weekStart, 'sample-meals'],
    queryFn: async () => {
      if (!service || !siteCode || !weekStart) return null;
      return service.LogiController_listSampleMeals({
        queries: { site_code: siteCode, week_start: weekStart },
      });
    },
    enabled: open && !!service && !!siteCode && !!weekStart,
  });

  const visibleDays = useMemo(() => {
    const today = dayjs().startOf('day');
    return (data?.days ?? [])
      .filter((day) => {
        const date = day.photo_date ? dayjs(day.photo_date) : null;
        if (!date?.isValid()) return false;
        if (menuWeekDayFromJsDay(date.day()) > lastMenuWeekDay) return false;
        const photos = day.photos ?? [];
        const count = Math.max(day.count ?? 0, photos.length);
        const hasPhoto = count > 0 && !!coverPhoto(photos)?.url;
        const isDue = !date.startOf('day').isAfter(today);
        return isDue || hasPhoto;
      })
      .sort((a, b) => String(a.photo_date ?? '').localeCompare(String(b.photo_date ?? '')));
  }, [data?.days, lastMenuWeekDay]);

  const columns = columnsFor(visibleDays.length);
  const rangeLabel = formatRange(visibleDays);
  const viewerPhotos = useMemo(
    () => sortSampleMealPhotos(viewerDay?.photos ?? []).filter((photo) => !!photo.url),
    [viewerDay]
  );
  const viewerIndex = Math.min(photoIndex, Math.max(viewerPhotos.length - 1, 0));
  const viewerPhoto = viewerPhotos[viewerIndex];
  const viewerDate = viewerDay?.photo_date ? dayjs(viewerDay.photo_date) : null;
  const viewerDateLabel = viewerDate?.isValid() ? viewerDate.format('DD/MM/YYYY') : '';
  const viewerWeekday = viewerDate?.isValid() ? WEEKDAYS[viewerDate.day()] : '';
  const viewerRotation = viewerPhoto?.url ? (rotationByUrl[viewerPhoto.url] ?? 0) : 0;
  const viewerSideways = viewerRotation % 180 !== 0;

  const rotateViewer = useCallback(() => {
    const url = viewerPhoto?.url;
    if (!url) return;
    setRotationByUrl((current) => ({
      ...current,
      [url]: nextQuarterTurn(current[url] ?? 0),
    }));
  }, [viewerPhoto?.url]);

  useEffect(() => {
    if (!open) {
      setViewerDay(null);
      setPhotoIndex(0);
      setRotationByUrl({});
    }
  }, [open]);

  useEffect(() => {
    if (!open || !viewerDay) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' && viewerPhotos.length > 1) {
        setPhotoIndex((index) => Math.max(0, index - 1));
      }
      if (event.key === 'ArrowRight' && viewerPhotos.length > 1) {
        setPhotoIndex((index) => Math.min(viewerPhotos.length - 1, index + 1));
      }
      if (event.key === 'r' || event.key === 'R') rotateViewer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, viewerDay, viewerPhotos.length, rotateViewer]);

  const openViewer = (day: SampleMealDay) => {
    setPhotoIndex(0);
    setViewerDay(day);
  };

  const renderViewer = () => {
    if (!viewerPhoto?.url) return null;
    const hasMany = viewerPhotos.length > 1;

    return (
      <Box sx={{ px: 3, pb: 3 }}>
        <Box sx={{ position: 'relative' }}>
          {hasMany && (
            <IconButton
              aria-label="previous"
              disabled={viewerIndex <= 0}
              onClick={() => setPhotoIndex((index) => Math.max(0, index - 1))}
              sx={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <Icon icon={ChevronLeft} />
            </IconButton>
          )}
          <Box
            sx={{
              width: '100%',
              height: '68vh',
              containerType: 'size',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              borderRadius: 2,
              bgcolor: 'grey.100',
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              src={viewerPhoto.url}
              alt={viewerWeekday ? `${viewerWeekday} ${viewerDateLabel}` : viewerDateLabel}
              sx={{
                // 90°/270° đổi max rộng/cao để phần nhìn thấy vẫn nằm trong khung.
                maxWidth: viewerSideways ? '100cqh' : '100cqw',
                maxHeight: viewerSideways ? '100cqw' : '100cqh',
                width: 'auto',
                height: 'auto',
                minWidth: 0,
                minHeight: 0,
                objectFit: 'contain',
                display: 'block',
                flexShrink: 0,
                transform: viewerRotation ? `rotate(${viewerRotation}deg)` : 'none',
              }}
            />
          </Box>
          {hasMany && (
            <IconButton
              aria-label="next"
              disabled={viewerIndex >= viewerPhotos.length - 1}
              onClick={() =>
                setPhotoIndex((index) => Math.min(viewerPhotos.length - 1, index + 1))
              }
              sx={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <Icon icon={ChevronRight} />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  };

  const renderContent = () => {
    if (!weekStart) {
      return (
        <Typography textAlign="center" color="text.secondary" sx={{ px: 3, pb: 4 }}>
          <FormattedMessage id="map.noWeek" />
        </Typography>
      );
    }
    if (isFetching) return <MuiLoading />;
    if (isError) {
      return (
        <Stack spacing={2} alignItems="center" sx={{ px: 3, pb: 4 }}>
          <Typography color="error">
            <FormattedMessage id="map.sampleMealPhotosLoadError" />
          </Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            <FormattedMessage id="map.sampleMealPhotosRetry" />
          </Button>
        </Stack>
      );
    }
    if (!visibleDays.length) {
      return (
        <Typography textAlign="center" color="text.secondary" sx={{ px: 3, pb: 4 }}>
          <FormattedMessage id="map.sampleMealPhotosEmptyDay" />
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          px: 3,
          pb: 3,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'stretch',
          gap: `${GRID_GAP}px`,
        }}
      >
        {visibleDays.map((day) => {
          const photos = day.photos ?? [];
          const count = Math.max(day.count ?? 0, photos.length);
          const cover = coverPhoto(photos);
          const hasPhoto = count > 0 && !!cover?.url;
          const date = day.photo_date ? dayjs(day.photo_date) : null;
          const dateLabel = date?.isValid() ? date.format('DD/MM/YYYY') : '—';
          const weekday = date?.isValid() ? WEEKDAYS[date.day()] : '';

          return (
            <Box
              key={day.photo_date ?? dateLabel}
              component={hasPhoto ? 'button' : 'div'}
              type={hasPhoto ? 'button' : undefined}
              onClick={hasPhoto ? () => openViewer(day) : undefined}
              sx={{
                width: {
                  xs: '100%',
                  tablet:
                    columns === 1 ? 'min(100%, 420px)' : `calc((100% - ${GRID_GAP}px) / 2)`,
                  laptop: `calc((100% - ${GRID_GAP * (columns - 1)}px) / ${columns})`,
                },
                maxWidth: columns === 1 ? 420 : 360,
                boxSizing: 'border-box',
                flex: '0 0 auto',
                minWidth: 0,
                p: 0,
                m: 0,
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
                font: 'inherit',
                color: 'inherit',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'common.black',
                borderRadius: 2,
                overflow: 'hidden',
                cursor: hasPhoto ? 'pointer' : 'default',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                '&:hover': hasPhoto
                  ? {
                      borderColor: 'primary.main',
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                    }
                  : undefined,
                '&:focus-visible': hasPhoto
                  ? {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    }
                  : undefined,
              }}
            >
              {hasPhoto ? (
                <Box
                  component="img"
                  src={cover?.url ?? ''}
                  alt={weekday ? `${weekday} ${dateLabel}` : dateLabel}
                  sx={{
                    width: '100%',
                    aspectRatio: '16 / 10',
                    objectFit: 'cover',
                    display: 'block',
                    bgcolor: 'grey.100',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '16 / 10',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.50',
                    color: 'text.secondary',
                  }}
                >
                  <Typography variant="body2">
                    <FormattedMessage id="map.sampleMealPhotosNoData" />
                  </Typography>
                </Box>
              )}
              <Box sx={{ position: 'relative', px: 1.5, py: 1.25, textAlign: 'center' }}>
                <Typography fontWeight={700} lineHeight={1.5} noWrap>
                  {weekday || dateLabel}
                </Typography>
                {weekday && (
                  <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
                    {dateLabel}
                  </Typography>
                )}
                {hasPhoto && (
                  <Chip
                    size="small"
                    color="primary"
                    label={`${count} ảnh`}
                    sx={{
                      fontWeight: 700,
                      position: 'absolute',
                      right: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <MuiDialog
        open={open}
        onClose={() => {
          if (viewerDay) {
            setViewerDay(null);
            return;
          }
          onClose();
        }}
        maxWidth="desktop"
        fullWidth
        disablePortal
        disableCloseBtn
        titleAlign="center"
        PaperProps={{
          sx: {
            m: 2,
            width: '100%',
            maxWidth: 980,
          },
        }}
      >
        <Box sx={{ position: 'relative', px: 3, pt: 2, pb: 2 }}>
          {viewerDay && (
            <IconButton
              onClick={rotateViewer}
              aria-label="Xoay ảnh"
              title="Xoay ảnh"
              size="small"
              color="primary"
              sx={{ position: 'absolute', top: 8, left: 8 }}
            >
              <Icon icon={RotateCw} size={16} />
            </IconButton>
          )}
          <IconButton
            onClick={viewerDay ? () => setViewerDay(null) : onClose}
            aria-label={viewerDay ? 'Quay lại' : 'Đóng'}
            size="small"
            color="primary"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <Icon icon={X} size={16} />
          </IconButton>
          <Typography
            variant="h5"
            sx={{ textAlign: 'center', color: 'text.primary', fontWeight: 700, px: 4 }}
          >
            <FormattedMessage id="map.sampleMealPhotos" />
          </Typography>
          {(viewerDay || rangeLabel) && !isFetching && !isError && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 0.5 }}>
              {viewerDay
                ? viewerPhotos.length > 1
                  ? `${viewerWeekday} · ${viewerDateLabel} · ${viewerIndex + 1}/${viewerPhotos.length}`
                  : `${viewerWeekday}${viewerDateLabel ? ` · ${viewerDateLabel}` : ''}`
                : rangeLabel}
            </Typography>
          )}
        </Box>
        {viewerDay ? renderViewer() : renderContent()}
      </MuiDialog>
    </>
  );
};

export default SampleMealPhotosDialog;
